import { getPivotCacheDefinitionXml, getPivotTableXml, PivotTableSpec } from "./pivotTable";
import { columnNumberToName, escapeXml } from "./xml";
import { createZip, ZipEntry } from "./zip";

const XML_ENCODER = new TextEncoder();

/**
 * A cell value. Numbers and Dates are written as numeric cells, a formula as a formula with its
 * result, everything else as text.
 */
export type WorkbookCell = string | number | Date | WorkbookFormula | null | undefined;

/**
 * A formula, in Excel's English notation without the leading "=". `value` is its result as the
 * file is written: Excel shows it until it recalculates, and a pivot table reads it.
 */
export interface WorkbookFormula {
  formula: string;
  value: number | string;
}

export function isFormula(cell: WorkbookCell): cell is WorkbookFormula {
  return typeof cell === "object" && cell !== null && !(cell instanceof Date);
}
export type WorkbookRow = WorkbookCell[];

/** A PNG placed on a sheet, e.g. a logo in a report head. */
export interface WorkbookImage {
  /** Raw PNG bytes. */
  data: Uint8Array;
  /**
   * 0-based column the image's top-left corner sits in, on row 1. Only an image in column A grows
   * the first row to fit; one further right floats over the cells instead.
   */
  anchorColumn: number;
  widthPx: number;
  heightPx: number;
}

export interface SheetSpec {
  /** Tab name; sanitised to Excel's rules (at most 31 characters, none of `[]:*?/\`). */
  name: string;
  rows: WorkbookRow[];
  /**
   * 1-based row number holding the table header. That row is written bold, gets Excel's filter
   * dropdowns, and the panes are frozen below it. Omit when the sheet has no header row.
   */
  headerRow?: number;
  /**
   * Fixed decimals (0-3, clamped) per 0-based column index, for numeric cells. With `headerRow`
   * set they apply only to the rows below it, so numbers above the header (e.g. a report preamble)
   * keep the default styles; without it, to every row. A column left `undefined` shows whole
   * numbers as `#,##0` and anything else in Excel's General format (full precision, no rounding).
   */
  fractionDigits?: (number | undefined)[];
  /** 0-based columns whose dates are shown as a day, without the time. */
  dayColumns?: number[];
  image?: WorkbookImage;
  /** A pivot table on this sheet, over a table on another sheet. */
  pivotTable?: PivotTableSpec;
}

const MAX_SHEET_NAME_LENGTH = 31;
const MAX_FRACTION_DIGITS = 3;

const EMU_PER_PIXEL = 9525;
const POINTS_PER_PIXEL = 0.75;
/** Gap between the image and the anchor cell's edges, and the extra row height it needs. */
const IMAGE_OFFSET_PX = 4;

/**
 * Style ids as written into `xl/styles.xml`; 0 is the default and is left off the cell.
 *
 * There is no bold-number style: only data rows are read as numbers, and the header row is by
 * definition not one of them. Ids from `STYLE_FIXED_DIGITS` on are the fixed-decimal styles for
 * 0 to `MAX_FRACTION_DIGITS` digits; the day style follows them, so their ids stay put.
 */
const STYLE_DEFAULT = 0;
const STYLE_BOLD = 1;
const STYLE_INTEGER = 2;
const STYLE_NUMBER = 3;
const STYLE_DATE = 4;
const STYLE_FIXED_DIGITS = 5;
const STYLE_DAY = STYLE_FIXED_DIGITS + 4;

/** Built-in short date, shown in the reader's own date order. */
const NUMBER_FORMAT_ID_DAY = 14;

/** Built-in OOXML number formats — no `<numFmt>` declaration needed for these ids. */
const NUMBER_FORMAT_ID_INTEGER = 3; // "#,##0"
const NUMBER_FORMAT_ID_GENERAL = 0; // "General"

/**
 * Custom number formats; ids below 164 are reserved for OOXML built-ins. The codes are invariant
 * (`,` groups, `.` separates decimals) and Excel renders them in the reader's own locale.
 *
 * The date format is custom because no built-in id both reads correctly across locales and
 * includes a time component.
 */
const DATE_FORMAT_ID = 164;
const DATE_FORMAT_CODE = "yyyy-mm-dd hh:mm";
const FIXED_DIGITS_FORMAT_ID_BASE = 165;

/** The number format id for `#,##0` with this many decimals (0-3, clamped), e.g. for a pivot value. */
export function fixedDigitsNumFmtId(digits: number) {
  return FIXED_DIGITS_FORMAT_ID_BASE + Math.min(MAX_FRACTION_DIGITS, Math.max(0, Math.round(digits)));
}

/**
 * Excel's day-zero, in UTC. Excel serials carry no timezone and are displayed as-is, so the
 * serial is built from the date's local wall-clock time (see `dateToExcelSerial`).
 */
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 80;

/**
 * The XML parts of the workbook, in package order. The binary PNG media parts are not included;
 * `createWorkbook` adds them as `xl/media/image<n>.png`.
 *
 * Exported for tests: a malformed or inconsistent part — a style id the stylesheet does not
 * declare, a relationship pointing nowhere — makes Excel refuse the whole file, and that is not
 * something the caller finds out about until a user tries to open it.
 */
export function buildWorkbookParts(sheets: SheetSpec[]): { name: string; xml: string }[] {
  return buildPackage(sheets).parts;
}

export function createWorkbook(sheets: SheetSpec[]): Blob {
  const { parts, media } = buildPackage(sheets);
  const files: ZipEntry[] = [
    ...parts.map(part => ({ name: part.name, data: XML_ENCODER.encode(part.xml) })),
    ...media,
  ];

  return new Blob([createZip(files)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Every part of the package: the XML parts, and the binary PNG parts of the sheets that carry an
 * image. Images are numbered 1..k in sheet order; that one number names the drawing part, its
 * relationships and the PNG part.
 */
function buildPackage(sheets: SheetSpec[]): {
  parts: { name: string; xml: string }[];
  media: ZipEntry[];
} {
  if (sheets.length === 0) {
    throw new Error("A workbook needs at least one sheet");
  }
  const sheetNames = uniqueSheetNames(sheets.map(sheet => sheet.name));
  const drawingParts: { name: string; xml: string }[] = [];
  const pivotParts: { name: string; xml: string }[] = [];
  const media: ZipEntry[] = [];
  let pivotCount = 0;
  sheets.forEach((sheet, index) => {
    const relationships: SheetRelationship[] = [];

    if (sheet.image !== undefined) {
      const imageNumber = media.length + 1;
      relationships.push({ type: "drawing", target: `../drawings/drawing${imageNumber}.xml` });
      drawingParts.push(
        { name: `xl/drawings/drawing${imageNumber}.xml`, xml: getDrawingXml(sheet.image) },
        {
          name: `xl/drawings/_rels/drawing${imageNumber}.xml.rels`,
          xml: getDrawingRelationshipsXml(imageNumber),
        },
      );
      media.push({ name: `xl/media/image${imageNumber}.png`, data: sheet.image.data });
    }

    if (sheet.pivotTable !== undefined) {
      const pivot = sheet.pivotTable;
      const sourceName = sheetNames[pivot.sourceSheet];
      if (sourceName === undefined || pivot.fieldNames.length === 0) {
        throw new Error("A pivot table needs an existing source sheet and at least one column");
      }
      pivotCount += 1;
      relationships.push({ type: "pivotTable", target: `../pivotTables/pivotTable${pivotCount}.xml` });
      pivotParts.push(
        {
          name: `xl/pivotCache/pivotCacheDefinition${pivotCount}.xml`,
          xml: getPivotCacheDefinitionXml(pivot, sourceName),
        },
        {
          name: `xl/pivotTables/pivotTable${pivotCount}.xml`,
          xml: getPivotTableXml(pivot, pivotCount),
        },
        {
          name: `xl/pivotTables/_rels/pivotTable${pivotCount}.xml.rels`,
          xml: getPivotTableRelationshipsXml(pivotCount),
        },
      );
    }

    if (relationships.length > 0) {
      drawingParts.push({
        name: `xl/worksheets/_rels/sheet${index + 1}.xml.rels`,
        xml: getSheetRelationshipsXml(relationships),
      });
    }
  });

  const parts = [
    {
      name: "[Content_Types].xml",
      xml: getContentTypesXml(sheets.length, media.length, pivotCount),
    },
    { name: "_rels/.rels", xml: getRootRelationshipsXml() },
    { name: "xl/workbook.xml", xml: getWorkbookXml(sheetNames, pivotCount) },
    {
      name: "xl/_rels/workbook.xml.rels",
      xml: getWorkbookRelationshipsXml(sheets.length, pivotCount),
    },
    { name: "xl/styles.xml", xml: getStylesXml() },
    ...sheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      xml: buildSheetXml(sheet, index === 0),
    })),
    ...drawingParts,
    ...pivotParts,
  ];
  return { parts, media };
}

interface SheetRelationship {
  type: "drawing" | "pivotTable";
  target: string;
}

function getContentTypesXml(sheetCount: number, imageCount: number, pivotCount: number) {
  const sheetOverrides = Array.from(
    { length: sheetCount },
    (_unused, index) =>
      `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
  ).join("");
  const drawingOverrides = Array.from({ length: imageCount }, (_unused, index) => index + 1)
    .map(
      number =>
        `<Override PartName="/xl/drawings/drawing${number}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`,
    )
    .join("");
  const pivotOverrides = Array.from({ length: pivotCount }, (_unused, index) => index + 1)
    .map(
      number =>
        `<Override PartName="/xl/pivotCache/pivotCacheDefinition${number}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.pivotCacheDefinition+xml"/>` +
        `<Override PartName="/xl/pivotTables/pivotTable${number}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.pivotTable+xml"/>`,
    )
    .join("");
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
    '<Default Extension="xml" ContentType="application/xml"/>',
    imageCount > 0 ? '<Default Extension="png" ContentType="image/png"/>' : "",
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
    sheetOverrides,
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>',
    drawingOverrides,
    pivotOverrides,
    "</Types>",
  ].join("");
}

function getRootRelationshipsXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>',
    "</Relationships>",
  ].join("");
}

/**
 * Excel rejects a tab name over 31 characters, with any of `[]:*?/\`, or starting or ending with
 * an apostrophe; a name left empty falls back to `Sheet<n>`.
 */
function sanitizeSheetName(name: string, index: number) {
  const cleaned = name
    .replace(/[[\]:*?/\\]/g, " ")
    .replace(/^[\s']+|[\s']+$/g, "")
    .slice(0, MAX_SHEET_NAME_LENGTH)
    .replace(/[\s']+$/, "");
  return cleaned || `Sheet${index + 1}`;
}

/** Excel also refuses two tabs with the same name (case-insensitive); later ones get " (2)", " (3)", … */
function uniqueSheetNames(names: string[]) {
  const used = new Set<string>();
  return names.map((raw, index) => {
    const base = sanitizeSheetName(raw, index);
    let name = base;
    for (let n = 2; used.has(name.toLowerCase()); n++) {
      const suffix = ` (${n})`;
      name = `${base.slice(0, MAX_SHEET_NAME_LENGTH - suffix.length)}${suffix}`;
    }
    used.add(name.toLowerCase());
    return name;
  });
}

/** `sheetNames` already made unique and valid by `uniqueSheetNames`. */
function getWorkbookXml(sheetNames: string[], pivotCount: number) {
  const sheets = sheetNames
    .map(
      (name, index) =>
        `<sheet name="${escapeXml(name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`,
    )
    .join("");
  // The caches follow the stylesheet in the relationships, see `getWorkbookRelationshipsXml`.
  const pivotCaches =
    pivotCount === 0
      ? ""
      : `<pivotCaches>${Array.from(
          { length: pivotCount },
          (_unused, index) =>
            `<pivotCache cacheId="${index + 1}" r:id="rId${sheetNames.length + 2 + index}"/>`,
        ).join("")}</pivotCaches>`;
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ',
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    // Opens on the first sheet even after a reader saved the file on another one; the format wants
    // bookViews before sheets.
    '<bookViews><workbookView activeTab="0"/></bookViews>',
    `<sheets>${sheets}</sheets>`,
    pivotCaches,
    "</workbook>",
  ].join("");
}

/**
 * Sheets take `rId1`…`rId<n>`, matching `getWorkbookXml`; the stylesheet comes after them, then
 * one pivot cache per pivot table.
 */
function getWorkbookRelationshipsXml(sheetCount: number, pivotCount: number) {
  const sheets = Array.from(
    { length: sheetCount },
    (_unused, index) =>
      `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`,
  ).join("");
  const pivotCaches = Array.from(
    { length: pivotCount },
    (_unused, index) =>
      `<Relationship Id="rId${sheetCount + 2 + index}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/pivotCacheDefinition" Target="pivotCache/pivotCacheDefinition${index + 1}.xml"/>`,
  ).join("");
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    sheets,
    `<Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`,
    pivotCaches,
    "</Relationships>",
  ].join("");
}

function getPivotTableRelationshipsXml(pivotNumber: number) {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/pivotCacheDefinition" Target="../pivotCache/pivotCacheDefinition${pivotNumber}.xml"/>`,
    "</Relationships>",
  ].join("");
}

function buildSheetXml(sheet: SheetSpec, isActive: boolean) {
  const { rows, image } = sheet;
  const maxColumns = getMaxColumnCount(rows);
  const rowCount = Math.max(1, rows.length);
  const endCellRef = `${columnNumberToName(maxColumns)}${rowCount}`;
  const headerRowIndex = sheet.headerRow === undefined ? -1 : sheet.headerRow - 1;
  const numberStyles = getNumberStyles(sheet.fractionDigits);
  const dayColumns = new Set(sheet.dayColumns ?? []);
  const firstRowHeight =
    image !== undefined
      ? ` ht="${Math.ceil((image.heightPx + 2 * IMAGE_OFFSET_PX) * POINTS_PER_PIXEL)}" customHeight="1"`
      : "";

  // Widths are folded into the same loop that writes the cells, so a large export gets one pass
  // over `rows` instead of a separate pass just to measure column content.
  const widest = new Array<number>(maxColumns).fill(0);

  const rowXml = rows
    .map((row, rowIndex) => {
      const isHeaderRow = rowIndex === headerRowIndex;
      const rowNumberStyles = rowIndex > headerRowIndex ? numberStyles : [];
      const cells = row
        .map((rawValue, columnIndex) => {
          const cellRef = `${columnNumberToName(columnIndex + 1)}${rowIndex + 1}`;
          const measuredLength = measureCellLength(rawValue);
          if (measuredLength > widest[columnIndex]) {
            widest[columnIndex] = measuredLength;
          }

          if (rawValue === null || rawValue === undefined || rawValue === "") {
            return "";
          }

          if (rawValue instanceof Date) {
            if (Number.isNaN(rawValue.getTime())) {
              return "";
            }
            const serial = dateToExcelSerial(rawValue);
            const style = dayColumns.has(columnIndex) ? STYLE_DAY : STYLE_DATE;
            return `<c r="${cellRef}" s="${style}" t="n"><v>${serial}</v></c>`;
          }

          if (isFormula(rawValue)) {
            const formula = `<f>${escapeXml(rawValue.formula)}</f>`;
            if (typeof rawValue.value === "number") {
              const style = rowNumberStyles[columnIndex] ?? STYLE_NUMBER;
              return `<c r="${cellRef}" s="${style}">${formula}<v>${rawValue.value}</v></c>`;
            }
            return `<c r="${cellRef}" t="str">${formula}<v>${escapeXml(rawValue.value)}</v></c>`;
          }

          if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
            // Excel right-aligns numeric cells itself, so no alignment style is needed.
            const style =
              rowNumberStyles[columnIndex] ??
              (Number.isInteger(rawValue) ? STYLE_INTEGER : STYLE_NUMBER);
            return `<c r="${cellRef}" s="${style}" t="n"><v>${rawValue}</v></c>`;
          }

          const style = isHeaderRow ? ` s="${STYLE_BOLD}"` : "";
          const cellValue = escapeXml(String(rawValue));
          return `<c r="${cellRef}"${style} t="inlineStr"><is><t xml:space="preserve">${cellValue}</t></is></c>`;
        })
        .join("");

      const height = rowIndex === 0 ? firstRowHeight : "";
      return `<row r="${rowIndex + 1}"${height}>${cells}</row>`;
    })
    .join("");

  // Widths are unconditional: a column sized to its content is always more readable than the
  // default, and no caller has ever wanted the alternative.
  const colsXml = `<cols>${getColumnWidthsXml(widest)}</cols>`;

  // A filter needs a header row to label its dropdowns, and a header row outside the sheet would
  // produce an inverted range such as "A3:B1", which makes Excel declare the file unreadable. The
  // same check guards the frozen pane, which would otherwise hide nothing but empty rows.
  const headerRow = sheet.headerRow;
  const hasUsableHeaderRow = headerRow !== undefined && headerRow >= 1 && headerRow <= rowCount;
  const tabSelected = isActive ? ' tabSelected="1"' : "";
  const sheetView = hasUsableHeaderRow
    ? `<sheetView${tabSelected} workbookViewId="0"><pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft"/></sheetView>`
    : `<sheetView${tabSelected} workbookViewId="0"/>`;
  // The format requires autoFilter after sheetData, and drawing after autoFilter.
  const autoFilterXml = hasUsableHeaderRow
    ? `<autoFilter ref="A${headerRow}:${columnNumberToName(maxColumns)}${rowCount}"/>`
    : "";
  // The sheet's only relationship is its drawing, see `getSheetRelationshipsXml`.
  const drawingXml = image === undefined ? "" : '<drawing r:id="rId1"/>';

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ',
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    `<dimension ref="A1:${endCellRef}"/>`,
    `<sheetViews>${sheetView}</sheetViews>`,
    '<sheetFormatPr defaultRowHeight="15"/>',
    colsXml,
    `<sheetData>${rowXml}</sheetData>`,
    autoFilterXml,
    drawingXml,
    "</worksheet>",
  ].join("");
}

/** The fixed-decimal style per 0-based column, `undefined` where the column has none. */
function getNumberStyles(fractionDigits: SheetSpec["fractionDigits"]): (number | undefined)[] {
  return (fractionDigits ?? []).map(digits =>
    digits === undefined || !Number.isFinite(digits)
      ? undefined
      : STYLE_FIXED_DIGITS + Math.min(MAX_FRACTION_DIGITS, Math.max(0, Math.round(digits))),
  );
}

/** Displayed length of a cell, so a Date is sized by its date format rather than `toString()`. */
function measureCellLength(value: WorkbookCell | undefined): number {
  if (value instanceof Date) {
    return DATE_FORMAT_CODE.length;
  }
  if (isFormula(value)) {
    return String(value.value).length;
  }
  return String(value ?? "").length;
}

/**
 * Converts a JS `Date` to the serial day count Excel's date system uses. The local wall-clock
 * time is encoded, because Excel shows the serial without any timezone conversion.
 */
function dateToExcelSerial(date: Date): number {
  const localWallClockMs = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
  return (localWallClockMs - EXCEL_EPOCH_MS) / MS_PER_DAY;
}

/**
 * Counted with a loop rather than `Math.max(...rows.map(…))`: the spread passes one argument per
 * row and throws once a large export exceeds the argument limit. A loop also allocates nothing,
 * which `lodash`'s `max` would not avoid — it still needs the intermediate array of lengths.
 */
function getMaxColumnCount(rows: WorkbookRow[]) {
  let maxColumns = 1;
  for (const row of rows) {
    if (row.length > maxColumns) {
      maxColumns = row.length;
    }
  }
  return maxColumns;
}

function getColumnWidthsXml(widest: number[]) {
  return widest
    .map((length, columnIndex) => {
      const width = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, length + 2));
      const columnNumber = columnIndex + 1;
      return `<col min="${columnNumber}" max="${columnNumber}" width="${width}" bestFit="1" customWidth="1"/>`;
    })
    .join("");
}

function getStylesXml() {
  const fixedDigits = Array.from({ length: MAX_FRACTION_DIGITS + 1 }, (_unused, digits) => digits);
  const fixedDigitsNumFmts = fixedDigits
    .map(digits => {
      const code = digits === 0 ? "#,##0" : `#,##0.${"0".repeat(digits)}`;
      return `<numFmt numFmtId="${FIXED_DIGITS_FORMAT_ID_BASE + digits}" formatCode="${code}"/>`;
    })
    .join("");
  const fixedDigitsXfs = fixedDigits
    .map(
      digits =>
        `<xf numFmtId="${FIXED_DIGITS_FORMAT_ID_BASE + digits}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`,
    )
    .join("");

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    // numFmtId 0 (General) and 3 (#,##0) are OOXML built-ins and need no declaration here.
    `<numFmts count="${fixedDigits.length + 1}">`,
    `<numFmt numFmtId="${DATE_FORMAT_ID}" formatCode="${escapeXml(DATE_FORMAT_CODE)}"/>`,
    fixedDigitsNumFmts,
    "</numFmts>",
    '<fonts count="2">',
    '<font><sz val="11"/><name val="Calibri"/></font>',
    '<font><b/><sz val="11"/><name val="Calibri"/></font>',
    "</fonts>",
    '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>',
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>',
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>',
    `<cellXfs count="${STYLE_DAY + 1}">`,
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="${STYLE_DEFAULT}"/>`,
    '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>',
    `<xf numFmtId="${NUMBER_FORMAT_ID_INTEGER}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`,
    `<xf numFmtId="${NUMBER_FORMAT_ID_GENERAL}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`,
    `<xf numFmtId="${DATE_FORMAT_ID}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`,
    fixedDigitsXfs,
    `<xf numFmtId="${NUMBER_FORMAT_ID_DAY}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`,
    "</cellXfs>",
    "</styleSheet>",
  ].join("");
}

/**
 * A sheet's relationships, numbered in order. A drawing comes first, so it is `rId1` as
 * `buildSheetXml` refers to it; a pivot table is found through its relationship alone.
 */
function getSheetRelationshipsXml(relationships: SheetRelationship[]) {
  const items = relationships
    .map(
      ({ type, target }, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/${type}" Target="${target}"/>`,
    )
    .join("");
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    items,
    "</Relationships>",
  ].join("");
}

function getDrawingRelationshipsXml(imageNumber: number) {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/image${imageNumber}.png"/>`,
    "</Relationships>",
  ].join("");
}

/** One picture anchored at row 1 of `anchorColumn` with a fixed size (`oneCellAnchor`, sizes in EMU). */
function getDrawingXml(image: WorkbookImage) {
  const cx = Math.round(image.widthPx * EMU_PER_PIXEL);
  const cy = Math.round(image.heightPx * EMU_PER_PIXEL);
  const offset = Math.round(IMAGE_OFFSET_PX * EMU_PER_PIXEL);
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    "<xdr:oneCellAnchor>",
    `<xdr:from><xdr:col>${Math.max(0, Math.floor(image.anchorColumn))}</xdr:col><xdr:colOff>${offset}</xdr:colOff><xdr:row>0</xdr:row><xdr:rowOff>${offset}</xdr:rowOff></xdr:from>`,
    `<xdr:ext cx="${cx}" cy="${cy}"/>`,
    "<xdr:pic>",
    '<xdr:nvPicPr><xdr:cNvPr id="2" name="Logo"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr>',
    '<xdr:blipFill><a:blip r:embed="rId1"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill>',
    `<xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr>`,
    "</xdr:pic>",
    "<xdr:clientData/>",
    "</xdr:oneCellAnchor>",
    "</xdr:wsDr>",
  ].join("");
}

